import { PriceAlertResource, uniqueArray } from '@eih-netbroker/common';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { PriceAlert, priceAlertsAtom } from '../../modules/trading/states/priceAlertsAtom';
import { useInstrumentSearch } from './useInstrumentSearch';
import { useResource, useResourceHeaders } from './useResource';

export const usePriceAlerts = () => {
  const [priceAlerts, setPriceAlerts] = useAtom(priceAlertsAtom);
  const priceAlertResource = useResource(PriceAlertResource);
  const { getListedInstrumentList } = useInstrumentSearch();
  const { getResourceHeaderWithToken } = useResourceHeaders();

  const loadPriceAlerts = useCallback(async (): Promise<void> => {
    try {
      if (priceAlerts?.isLoading) {
        return;
      }

      setPriceAlerts({
        priceAlerts: [],
        isLoading: true,
        hasError: false,
      });

      const { data: loadedPriceAlerts } = await priceAlertResource.loadPriceAlerts(getResourceHeaderWithToken());
      const requiredRics = uniqueArray(loadedPriceAlerts.map(priceAlert => priceAlert.ric));
      const priceAlertInstruments = await getListedInstrumentList(requiredRics);
      if (priceAlertInstruments?.length !== requiredRics.length) {
        throw new Error('Failed to load all price alert instruments');
      }

      setPriceAlerts({
        priceAlerts: loadedPriceAlerts.map(priceAlert => ({
          ...priceAlert,
          instrument: priceAlertInstruments.find(instrument => instrument.ric === priceAlert.ric)!,
        })),
        isLoading: false,
      });
    } catch (err) {
      console.error(`Failed to load price alerts`, err);
      setPriceAlerts({
        priceAlerts: [],
        isLoading: false,
        hasError: true,
      });
    }
  }, [setPriceAlerts, getResourceHeaderWithToken, getListedInstrumentList, priceAlertResource, priceAlerts]);

  const savePriceAlert = useCallback(
    async (priceAlert: PriceAlert) => {
      if (priceAlert.id) {
        await priceAlertResource.updatePriceAlert(
          {
            priceAlertId: priceAlert.id,
            priceAlertCreateModify: {
              ...priceAlert,
              ric: priceAlert.instrument.ric,
              currency: priceAlert.instrument.currency,
              ticker: priceAlert.instrument.ticker,
            },
          },
          getResourceHeaderWithToken(),
        );

        setPriceAlerts(prev => ({
          ...prev,
          priceAlerts: prev.priceAlerts.map(alert => (alert.id === priceAlert.id ? priceAlert : alert)),
        }));
      } else {
        const response = await priceAlertResource.createPriceAlert(
          {
            ...priceAlert,
            ric: priceAlert.instrument.ric,
            currency: priceAlert.instrument.currency,
            ticker: priceAlert.instrument.ticker,
          },
          getResourceHeaderWithToken(),
        );

        const createdPriceAlert: PriceAlert = { ...priceAlert, id: response.data.id };
        setPriceAlerts(prev => ({
          ...prev,
          priceAlerts: [...prev.priceAlerts, { ...priceAlert, id: createdPriceAlert.id }],
        }));
      }
    },
    [getResourceHeaderWithToken, priceAlertResource, setPriceAlerts],
  );

  const deletePriceAlert = useCallback(
    async (id: number) => {
      try {
        const res = await priceAlertResource.deletePriceAlert(id, getResourceHeaderWithToken());
        setPriceAlerts(prev => ({
          ...prev,
          priceAlerts: prev.priceAlerts.filter(priceAlert => priceAlert.id !== id),
        }));
        return res;
      } catch (err) {
        throw err;
      }
    },
    [priceAlertResource, getResourceHeaderWithToken, setPriceAlerts],
  );

  return {
    priceAlerts,
    loadPriceAlerts,
    savePriceAlert,
    deletePriceAlert,
  };
};
